import { textUtils } from '@sato/components';
import { action, computed, makeObservable, observable } from 'mobx';
import { create, persist } from 'mobx-persist';

import { parkingApi } from '@api/parking.api';
import { ParkingProduct } from '@models/parking.types';
import { ParkingSpaceRentingFormType } from '@views/parking/pages/ParkingSpaceRentingPage/components/ParkingSpaceRentingForm';

import { contractStore } from '../contractStore';
import { localizationStore } from '../localization';

import { ParkingStoreBase } from './parkingStoreBase';

type ParkingProductSubmitResult = 'SUCCESS' | 'STRONG_AUTH_REQUIRED';

/** Stores data related to parking space renting */
export class ParkingRentingStore extends ParkingStoreBase {
  /** Persist form values so that we can refer to them in summary, and when returning from strong authentication */
  @persist('object') formValues: ParkingSpaceRentingFormType | null = null;

  setFormValues(newValues: typeof this.formValues) {
    this.formValues = newValues;
  }

  constructor() {
    super();
    // NOTE: use makeObservable instead of makeAutoObservable since the latter doesn't work when using class inheritance
    makeObservable(this, {
      // from this class
      formValues: observable,
      setFormValues: action,
      submitAgreement: action,
      createOrUpdateDraftAgreement: action,

      // from inherited ParkingStoreBase class
      draftAgreementId: observable,
      lockEndDate: observable,
      lockedParkingProduct: observable,
      hasOngoingLock: computed,
      agreementConfirmationResponse: observable,
      abortRentingAndUnlockProduct: action,
      clear: action,
      startRentingProcess: action,
    });
  }

  /** Locks the product, thus starting the renting process and rent timer */
  async startRentingProcess(parkingProduct: ParkingProduct) {
    return this.lockProduct(parkingProduct);
  }

  /** Creates a new draft agreement or updates previously created one. */
  async createOrUpdateDraftAgreement(
    formValues: Pick<ParkingSpaceRentingFormType, 'startDate'>
  ) {
    if (!this.lockedParkingProduct)
      throw Error('lockedParkingProduct is required');
    if (!contractStore.selectedContractId)
      throw Error('selectedContractId is required');

    const commonProps = {
      productId: this.lockedParkingProduct.id,
      rentDate: formValues.startDate,
      language: textUtils.toUpperCase(localizationStore.language),
      apartmentContractId: contractStore.selectedContractId,
    };

    const { data } = await (this.draftAgreementId
      ? // Update existing agreement draft if it was previously created
        parkingApi.updateAndFetchParkingAgreementDraft({
          ...commonProps,
          offerId: this.draftAgreementId,
        })
      : // Create a new parking agreement draft
        parkingApi.createAndFetchParkingAgreementDraft(commonProps));

    this.draftAgreementId = data.offerId;

    return data;
  }

  /** Submits the parking agreement. Clears the state and sets submittedForm if successful. */
  async submitAgreement(
    values: ParkingSpaceRentingFormType
  ): Promise<ParkingProductSubmitResult> {
    if (!this.lockedParkingProduct) throw Error('Parking Product is missing');

    this.formValues = values;

    // Create or update the draft agreement to make sure it exists and is up to date with form values
    const { offerId, strongAuthenticationRequired } =
      await this.createOrUpdateDraftAgreement(values);

    if (strongAuthenticationRequired) return 'STRONG_AUTH_REQUIRED';

    const response = await parkingApi.createParkingAgreement({ offerId });

    // Optimistically add a parking contract to contract store. Creating a contract takes around a minute
    // in the backend so we can't fetch the contract information from API at this point.
    contractStore.addQuickRentedParkingContractFromParkingProduct({
      parkingProduct: this.lockedParkingProduct,
      contractId: offerId,
      contractStartingDate: values.startDate,
    });

    this.agreementConfirmationResponse = response.data;

    // Clear lock end date but keep lockedParkingProduct so that we can show it in summary page
    this.lockEndDate = null;

    return 'SUCCESS';
  }

  override clear() {
    super.clear();
    // Clear store values
    this.formValues = null;
  }
}

export const parkingRentingStore = new ParkingRentingStore();

create()('parking-renting-store', parkingRentingStore);
